import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@mantine/hooks";
import { CurrencyCode } from "@/types";
import { useGetInspectionPreview } from "@/services/Inspections";
import { getApiError } from "@/utils/apiErrors";
import { InspectionContext } from "./types";
import { InspectionFormValues, toPreviewInspection } from "./useInspectionForm";

/** How long the inspector has to stop typing before the deviations are priced again. */
const PREVIEW_DELAY_MS = 400;

/** The original order's deviation costs as the backend priced them, in the order's currency. */
export interface DeviationCosts {
  currency: CurrencyCode;
  /** Per row of each table. */
  products: number[];
  services: number[];
  /** The value adjustment for the deviation products. */
  productsTotal: number;
  servicesTotal: number;
  total: number;
  /** Invoiced at the contract price. */
  contractWeightKg: number;
  /** The deviation products together, invoiced at their own prices. */
  deductedWeightKg: number;
}

export interface DeviationPreview {
  /** Null until every deviation row is filled in (photos aside) and the backend has priced them. */
  costs: DeviationCosts | null;
  /** True while a row is unfinished, so there's nothing to price yet. */
  isIncomplete: boolean;
  /** Why the backend couldn't price the deviations. */
  error: string | null;
}

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

/**
 * Prices the original order's deviations with the backend's preview while the inspector fills them in. Costs are
 * never calculated here. The preview lists the products in row order, then the services, so its lines are matched
 * to the rows by position.
 */
export const useDeviationPreview = (
  orderId: number,
  values: InspectionFormValues,
  context: InspectionContext
): DeviationPreview => {
  const { t } = useTranslation();
  const input = useMemo(() => toPreviewInspection(values, context), [values, context]);
  const [debouncedInput] = useDebouncedValue(input, PREVIEW_DELAY_MS);
  // Nothing is priced while a row is unfinished; otherwise once the inspector has stopped typing.
  const preview = useGetInspectionPreview(orderId, input && debouncedInput);

  const part = input ? preview.data?.parts[0] : undefined;
  const productCount = input?.products.length ?? 0;
  const serviceCount = input?.services.length ?? 0;
  // A result for other rows (one was just added or removed) can't be matched to these, so it isn't shown.
  const matches = !!part && part.deviations.length === productCount + serviceCount;

  const costs = useMemo<DeviationCosts | null>(() => {
    if (!part || !matches) return null;
    const lineCosts = part.deviations.map((line) => line.cost);
    const products = lineCosts.slice(0, productCount);
    const services = lineCosts.slice(productCount);
    return {
      currency: part.currencyType,
      products,
      services,
      productsTotal: sum(products),
      servicesTotal: sum(services),
      total: sum(lineCosts),
      contractWeightKg: part.contractWeightKg,
      deductedWeightKg: part.deductedWeightKg,
    };
  }, [part, matches, productCount]);

  const apiError = input && preview.isError ? getApiError(preview.error) : null;
  const error = !apiError
    ? null
    : apiError.errorCode === 181 && apiError.reason
      ? t("inspections:costs.previewInvalid", { reason: apiError.reason })
      : t("inspections:costs.previewFailed");

  const hasRows = values.hasDeviation && values.deviationProducts.length + values.deviationServices.length > 0;

  return { costs: error ? null : costs, isIncomplete: hasRows && !input, error };
};
